import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Tag } from '../../interfaces/api/tag';
import { fetchTags, updateTag } from '../services/tags.service';
import { TagFilterParams, TagsParams } from '../../api/tags';

interface TagState {
	tags: {
		byId: Record<number, Tag>;
		all: Tag[];
	};
	isLoading: boolean;
}

const initialState = {
	tags: {
		byId: {},
		all: [],
	},
	isLoading: false,
} as TagState;

export const fetchTagsThunk = createAsyncThunk<Tag[], { params: TagFilterParams }>(
	'tag/fetchTags',
	async ({ params }, { rejectWithValue }) => {
		try {
			return await fetchTags(params);
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const updateTagThunk = createAsyncThunk<Tag, { id: number; param: TagsParams }>(
	'tag/updateTag',
	async ({ id, param }, { rejectWithValue }) => {
		try {
			return await updateTag(id, param);
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

const tagSlice = createSlice({
	name: 'tag',
	initialState,
	reducers: {
		resetTag: (state) => {
			Object.assign(state, initialState);
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchTagsThunk.fulfilled, (state, action) => {
			const byId: Record<number, Tag> = {};
			action.payload.forEach((tag) => {
				byId[tag.id] = tag;
			});
			const all = action.payload.map((tag) => tag);
			state.tags = { byId, all };
			state.isLoading = false;
		});
		builder.addCase(fetchTagsThunk.pending, (state) => {
			state.isLoading = true;
		});
		builder.addCase(fetchTagsThunk.rejected, (state, action) => {
			state.tags = {
				byId: {},
				all: [],
			};
			state.isLoading = false;
		});

		builder.addCase(updateTagThunk.fulfilled, (state, action) => {
			state.tags.byId[action.payload.id] = action.payload;
			state.isLoading = false;
		});
		builder.addCase(updateTagThunk.pending, (state) => {
			state.isLoading = true;
		});
		builder.addCase(updateTagThunk.rejected, (state) => {
			state.isLoading = false;
		});
	},
});

export const { resetTag } = tagSlice.actions;

export default tagSlice;
